// Diálogos do contrato: gerar documento no DNA Sign + registrar aditivo
import React, { useState, useEffect } from 'react';
import { Loader2, FileSignature, PlusCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = window.location.origin;
const authHeaders = () => ({ 'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}` });

export const GerarDnaSignDialog = ({ open, onOpenChange, contract, onDone }) => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [send, setSend] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/dna-sign/templates`, { headers: authHeaders() });
        const json = await res.json();
        setTemplates(json.items || []);
      } catch { toast.error('Não foi possível carregar os modelos do DNA Sign'); }
    })();
  }, [open]);

  const gerar = async () => {
    if (!templateId) { toast.error('Selecione o modelo do documento'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/student-contracts/contract/${contract.id}/dna-sign`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ template_id: templateId, send }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Falha ao gerar documento');
      toast.success(send ? `Documento ${json.document_id} gerado e enviado para assinatura`
                         : `Documento ${json.document_id} gerado`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dnasign-gerar-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileSignature className="w-4 h-4 text-violet-600" />
            Gerar contrato no DNA Sign · {contract?.academic_year}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Modelo do documento</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="dnasign-template-select">
                <SelectValue placeholder="Selecione o modelo do DNA Sign" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">
              Usa os dados já consolidados do contrato {contract?.contract_number} (aluno, responsável,
              turma, plano e valores).
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={send} onChange={e => setSend(e.target.checked)}
                   className="w-4 h-4 accent-violet-600" data-testid="dnasign-send-now" />
            Enviar para assinatura imediatamente
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={gerar} disabled={busy} className="bg-violet-600 hover:bg-violet-700"
                    data-testid="dnasign-gerar-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gerar documento'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CATEGORIAS = [
  ['mensalidade', 'Mensalidade'], ['matricula', 'Matrícula/Rematrícula'], ['anuidade', 'Anuidade'],
  ['material', 'Material'], ['bilingue', 'Bilíngue'], ['integral', 'Integral'], ['almoco', 'Almoço'],
];

export const AditivoDialog = ({ open, onOpenChange, contract, onDone }) => {
  const [form, setForm] = useState({ tipo: 'financeiro', categoria: '', valor_delta: '', descricao: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ tipo: 'financeiro', categoria: '', valor_delta: '', descricao: '' }); }, [open]);

  const salvar = async () => {
    if (!form.descricao?.trim()) { toast.error('Descreva o aditivo'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/student-contracts/contract/${contract.id}/addendum`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          descricao: form.descricao.trim(),
          tipo: form.tipo,
          categoria: form.categoria || null,
          valor_delta: form.valor_delta ? Number(String(form.valor_delta).replace(',', '.')) : 0,
          parcelas: form.parcelas ? Number(form.parcelas) : null,
          dia_vencimento: form.dia_vencimento ? Number(form.dia_vencimento) : null,
          vigencia_inicio: form.vigencia_inicio || null,
          motivo: form.motivo || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Falha ao registrar aditivo');
      toast.success(`Aditivo ${json.addendum.numero} registrado — valor original preservado`);
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="aditivo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <PlusCircle className="w-4 h-4 text-violet-600" />
            Registrar aditivo · Contrato {contract?.contract_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[11.5px] text-slate-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            O contrato assinado não é alterado. O aditivo é registrado à parte e os valores em vigor
            passam a mostrar contrato + aditivos.
          </p>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Descrição do aditivo *</Label>
            <Input value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                   placeholder="Ex.: Inclusão de Período Integral a partir de março"
                   data-testid="aditivo-descricao" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Tipo</Label>
              <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
                <SelectTrigger data-testid="aditivo-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="financeiro">Financeiro</SelectItem>
                  <SelectItem value="servico">Serviço</SelectItem>
                  <SelectItem value="academico">Acadêmico</SelectItem>
                  <SelectItem value="outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Categoria afetada</Label>
              <Select value={form.categoria} onValueChange={v => setForm(f => ({ ...f, categoria: v }))}>
                <SelectTrigger data-testid="aditivo-categoria"><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIAS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Valor (+/-)</Label>
              <Input value={form.valor_delta} onChange={e => setForm(f => ({ ...f, valor_delta: e.target.value }))}
                     placeholder="450 ou -120" data-testid="aditivo-valor" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Parcelas</Label>
              <Input type="number" value={form.parcelas || ''} onChange={e => setForm(f => ({ ...f, parcelas: e.target.value }))}
                     placeholder="Opcional" data-testid="aditivo-parcelas" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Dia de vencimento</Label>
              <Input type="number" value={form.dia_vencimento || ''} onChange={e => setForm(f => ({ ...f, dia_vencimento: e.target.value }))}
                     placeholder="Opcional" data-testid="aditivo-vencimento" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Vigência a partir de</Label>
              <Input type="date" value={form.vigencia_inicio || ''} onChange={e => setForm(f => ({ ...f, vigencia_inicio: e.target.value }))}
                     data-testid="aditivo-vigencia" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Motivo / justificativa</Label>
            <Textarea rows={2} value={form.motivo || ''} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}
                      placeholder="Registrado na auditoria do contrato" data-testid="aditivo-motivo" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={salvar} disabled={busy} className="bg-violet-600 hover:bg-violet-700"
                    data-testid="aditivo-salvar">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Registrar aditivo'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
